import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Account settings: your picture, email, password, the devices signed in to
// your account, two-step login, and a link to the security log.
// (Each companion's own settings live at /chat-settings/:companionId.)

/* ── round photo cropper (same one as companion settings) ── */
function CropModal({ imageSrc, onConfirm, onCancel }) {
  const canvasRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [lastPos, setLastPos] = useState(null);
  const imgRef = useRef(null);
  const lastPinchDist = useRef(null);
  const SIZE = 280;

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const sc = Math.max(SIZE / img.width, SIZE / img.height);
      setScale(sc);
      setOffset({ x: (SIZE - img.width * sc) / 2, y: (SIZE - img.height * sc) / 2 });
    };
    img.src = imageSrc;
  }, [imageSrc]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.drawImage(imgRef.current, offset.x, offset.y, imgRef.current.width * scale, imgRef.current.height * scale);
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(201,147,90,0.6)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [offset, scale]);

  const getPos = (e) => {
    const touch = e.touches?.[0];
    return { x: touch ? touch.clientX : e.clientX, y: touch ? touch.clientY : e.clientY };
  };
  const onStart = (e) => {
    if (e.touches?.length === 2) return;
    setDragging(true);
    setLastPos(getPos(e));
  };
  const onMove = (e) => {
    if (e.touches?.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (lastPinchDist.current !== null) {
        const delta = dist - lastPinchDist.current;
        setScale(prev => Math.max(0.3, Math.min(5, prev + delta * 0.005)));
      }
      lastPinchDist.current = dist;
      return;
    }
    if (!dragging || !lastPos) return;
    const pos = getPos(e);
    setOffset(prev => ({ x: prev.x + pos.x - lastPos.x, y: prev.y + pos.y - lastPos.y }));
    setLastPos(pos);
  };
  const onEnd = () => {
    setDragging(false);
    setLastPos(null);
    lastPinchDist.current = null;
  };
  const onWheel = (e) => {
    e.preventDefault();
    setScale(prev => Math.max(0.3, Math.min(5, prev - e.deltaY * 0.001)));
  };
  const confirm = () => {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(imgRef.current, offset.x, offset.y, imgRef.current.width * scale, imgRef.current.height * scale);
    canvas.toBlob(blob => onConfirm(blob), 'image/jpeg', 0.92);
  };

  return (
    <div style={cropStyles.overlay}>
      <div style={cropStyles.modal}>
        <div style={cropStyles.title}>drag to adjust</div>
        <div style={cropStyles.hint}>drag to reposition • pinch or scroll to zoom</div>
        <div style={cropStyles.canvasWrap}>
          <canvas
            ref={canvasRef} width={SIZE} height={SIZE} style={cropStyles.canvas}
            onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
            onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} onWheel={onWheel}
          />
        </div>
        <div style={cropStyles.actions}>
          <button style={cropStyles.cancelBtn} onClick={onCancel}>cancel</button>
          <button style={cropStyles.confirmBtn} onClick={confirm}>use photo</button>
        </div>
      </div>
    </div>
  );
}

const cropStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#1e1825', borderRadius: '24px', border: '1px solid rgba(201,147,90,0.15)', padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', width: '340px', maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box' },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: '#e8ddd0', fontWeight: '300', fontStyle: 'italic' },
  hint: { fontSize: '12px', color: '#4a3d30', fontWeight: '300' },
  canvasWrap: { borderRadius: '50%', overflow: 'hidden', boxShadow: '0 0 0 2px rgba(201,147,90,0.2)' },
  canvas: { display: 'block', cursor: 'grab', touchAction: 'none' },
  actions: { display: 'flex', gap: '12px', width: '100%' },
  cancelBtn: { flex: 1, padding: '13px', borderRadius: '14px', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#4a3d30', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer' },
  confirmBtn: { flex: 1, padding: '13px', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer' },
};

/* ── helpers ── */
const deviceName = (ua) => {
  if (!ua) return 'unknown device';
  const u = ua.toLowerCase();
  if (/bot|crawl|spider|curl|wget|python|go-http|headless/.test(u)) return 'bot or script';
  const os = /iphone/.test(u) ? 'iPhone' : /ipad/.test(u) ? 'iPad' : /android/.test(u) ? 'Android'
    : /mac os x/.test(u) ? 'Mac' : /windows/.test(u) ? 'Windows' : /linux/.test(u) ? 'Linux' : '';
  const br = /edg\//.test(u) ? 'Edge' : /crios|chrome\//.test(u) ? 'Chrome' : /fxios|firefox/.test(u) ? 'Firefox'
    : /safari/.test(u) ? 'Safari' : '';
  return [br, os].filter(Boolean).join(' on ') || 'unknown device';
};

const ago = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 5) return 'active now';
  if (mins < 60) return `active ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `active ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `active ${days}d ago`;
};

const shortDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
  : '');

const placeName = (loc) => {
  if (!loc) return '';
  if (loc.country === 'private network') return 'private network';
  return [loc.city, loc.region, loc.country].filter(Boolean).join(', ');
};

// "ABCDEFGH..." -> "ABCD EFGH ..." so it's easier to type into an app
const groupKey = (key) => (key || '').replace(/(.{4})/g, '$1 ').trim();

export default function Settings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [account, setAccount] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [toast, setToast] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // picture
  const [cropSrc, setCropSrc] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  // email
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  // devices
  const [confirmSignOutOthers, setConfirmSignOutOthers] = useState(false);

  // two-step login
  const [tfStage, setTfStage] = useState('idle');   // idle | setup | codes | disable | regen
  const [tfSecret, setTfSecret] = useState('');
  const [tfUrl, setTfUrl] = useState('');
  const [tfCode, setTfCode] = useState('');
  const [tfPassword, setTfPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [tfBusy, setTfBusy] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadAccount();
    loadSessions();
  }, []);

  const describeError = (err) => {
    const d = err?.response?.data?.detail;
    if (typeof d === 'string') return d;
    return err?.message || 'something went wrong';
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const loadAccount = () => {
    axios.get(`${API}/account?token=${token}`)
      .then(res => setAccount(res.data))
      .catch(err => setErrorMsg(`couldn't load your account: ${describeError(err)}`));
  };

  const loadSessions = () => {
    axios.get(`${API}/account/sessions?token=${token}`)
      .then(res => setSessions(res.data.sessions || []))
      .catch(err => setErrorMsg(`couldn't load your devices: ${describeError(err)}`));
  };

  const copy = (text, label) => {
    const done = () => showToast(`${label} copied`);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => showToast('couldn\'t copy — select it and copy by hand'));
    } else {
      showToast('couldn\'t copy — select it and copy by hand');
    }
  };

  // ---- picture ----
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropConfirm = (blob) => {
    setCropSrc(null);
    setAvatarPreview(URL.createObjectURL(blob));
    const formData = new FormData();
    formData.append('file', blob, 'avatar.jpg');
    axios.post(`${API}/account/avatar?token=${token}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(res => {
        setAccount(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
        setAvatarPreview(null);
        showToast('picture updated');
      })
      .catch(err => {
        setAvatarPreview(null);
        setErrorMsg(`couldn't upload the picture: ${describeError(err)}`);
      });
  };

  const removeAvatar = () => {
    axios.delete(`${API}/account/avatar?token=${token}`)
      .then(() => { setAccount(prev => ({ ...prev, avatar_url: null })); showToast('picture removed'); })
      .catch(err => setErrorMsg(`couldn't remove the picture: ${describeError(err)}`));
  };

  // ---- email ----
  const saveEmail = () => {
    setSavingEmail(true);
    setErrorMsg('');
    axios.put(`${API}/account/email`, { token, new_email: newEmail, current_password: emailPassword })
      .then(res => {
        setAccount(prev => ({ ...prev, email: res.data.email }));
        setEditingEmail(false);
        setNewEmail('');
        setEmailPassword('');
        showToast('email changed — use it next time you sign in');
      })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setSavingEmail(false));
  };

  // ---- password ----
  const pwMismatch = newPw && confirmPw && newPw !== confirmPw;
  const pwReady = currentPw && newPw.length >= 8 && newPw === confirmPw;

  const savePassword = () => {
    if (!pwReady) return;
    setSavingPw(true);
    setErrorMsg('');
    axios.put(`${API}/account/password`, { token, current_password: currentPw, new_password: newPw })
      .then(res => {
        setCurrentPw(''); setNewPw(''); setConfirmPw('');
        const n = res.data.signed_out_devices || 0;
        showToast(n ? `password changed — signed out ${n} other device${n === 1 ? '' : 's'}` : 'password changed');
        loadSessions();
      })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setSavingPw(false));
  };

  // ---- devices ----
  const signOutDevice = (session) => {
    axios.delete(`${API}/account/sessions/${session.id}?token=${token}`)
      .then(() => {
        if (session.is_current) {
          localStorage.clear();
          navigate('/login');
          return;
        }
        showToast('device signed out');
        loadSessions();
      })
      .catch(err => setErrorMsg(`couldn't sign that device out: ${describeError(err)}`));
  };

  const signOutOthers = () => {
    axios.post(`${API}/account/sessions/revoke-others`, { token })
      .then(res => {
        setConfirmSignOutOthers(false);
        const n = res.data.signed_out_devices || 0;
        showToast(n ? `signed out ${n} other device${n === 1 ? '' : 's'}` : 'no other devices were signed in');
        loadSessions();
      })
      .catch(err => setErrorMsg(`couldn't sign out other devices: ${describeError(err)}`));
  };

  const logout = () => {
    axios.post(`${API}/auth/logout`, { token }).catch(() => {}).finally(() => {
      localStorage.clear();
      navigate('/login');
    });
  };

  // ---- two-step login ----
  const resetTf = () => {
    setTfStage('idle'); setTfSecret(''); setTfUrl(''); setTfCode(''); setTfPassword('');
  };

  const startTwoFactor = () => {
    setTfBusy(true);
    setErrorMsg('');
    axios.post(`${API}/account/2fa/setup`, { token })
      .then(res => { setTfSecret(res.data.secret); setTfUrl(res.data.otpauth_url); setTfStage('setup'); })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setTfBusy(false));
  };

  const enableTwoFactor = () => {
    setTfBusy(true);
    setErrorMsg('');
    axios.post(`${API}/account/2fa/enable`, { token, code: tfCode })
      .then(res => {
        setBackupCodes(res.data.backup_codes || []);
        setTfStage('codes');
        setTfSecret(''); setTfUrl(''); setTfCode('');
        loadAccount();
      })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setTfBusy(false));
  };

  const disableTwoFactor = () => {
    setTfBusy(true);
    setErrorMsg('');
    axios.post(`${API}/account/2fa/disable`, { token, current_password: tfPassword })
      .then(() => { resetTf(); loadAccount(); showToast('two-step login turned off'); })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setTfBusy(false));
  };

  const regenerateCodes = () => {
    setTfBusy(true);
    setErrorMsg('');
    axios.post(`${API}/account/2fa/backup-codes`, { token, current_password: tfPassword })
      .then(res => { setBackupCodes(res.data.backup_codes || []); setTfPassword(''); setTfStage('codes'); loadAccount(); })
      .catch(err => setErrorMsg(describeError(err)))
      .finally(() => setTfBusy(false));
  };

  const currentAvatar = avatarPreview || account?.avatar_url;
  const initial = (account?.email || localStorage.getItem('username') || '?')[0].toUpperCase();
  const otherSessions = sessions.filter(x => !x.is_current);

  return (
    <div style={s.container}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />
      {cropSrc && <CropModal imageSrc={cropSrc} onConfirm={handleCropConfirm} onCancel={() => setCropSrc(null)} />}

      <div style={s.header}>
        <button style={s.backBtn} onClick={() => navigate('/companions')} aria-label="back">←</button>
        <div style={s.headerTitle}>settings</div>
      </div>

      <div style={s.content}>
        {errorMsg && (
          <div style={s.errorBanner} onClick={() => setErrorMsg('')}>⚠ {errorMsg}</div>
        )}

        {/* PROFILE */}
        <div style={s.section}>
          <div style={s.sectionLabel}>profile</div>
          <div style={s.card}>
            <div style={s.profileRow}>
              <div style={s.avatarWrap} onClick={() => fileInputRef.current?.click()} title="change picture">
                <div style={s.avatar}>
                  {currentAvatar ? <img src={currentAvatar} alt="you" style={s.avatarImg} /> : initial}
                </div>
                <div style={s.avatarBadge}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0e0b0f" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={s.email}>{account?.email || '…'}</div>
                <div style={s.hint}>
                  tap the picture to change it
                  {account?.avatar_url && (
                    <> · <button style={s.linkBtn} onClick={removeAvatar}>remove</button></>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EMAIL */}
        <div style={s.section}>
          <div style={s.sectionLabel}>email</div>
          <div style={s.card}>
            {!editingEmail ? (
              <div style={s.rowBetween}>
                <div style={s.value}>{account?.email || '…'}</div>
                <button style={s.smallBtn} onClick={() => { setEditingEmail(true); setNewEmail(''); setEmailPassword(''); }}>change</button>
              </div>
            ) : (
              <div style={s.form}>
                <input style={s.input} type="email" placeholder="new email" autoComplete="email"
                  value={newEmail} onChange={e => setNewEmail(e.target.value)} />
                <input style={s.input} type="password" placeholder="your current password" autoComplete="current-password"
                  value={emailPassword} onChange={e => setEmailPassword(e.target.value)} />
                <div style={s.row}>
                  <button style={newEmail && emailPassword && !savingEmail ? s.primaryBtn : s.primaryBtnOff}
                    onClick={saveEmail} disabled={!newEmail || !emailPassword || savingEmail}>
                    {savingEmail ? 'saving…' : 'save email'}
                  </button>
                  <button style={s.quietBtn} onClick={() => setEditingEmail(false)}>cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* PASSWORD */}
        <div style={s.section}>
          <div style={s.sectionLabel}>password</div>
          <div style={s.card}>
            <div style={s.form}>
              <input style={s.input} type="password" placeholder="current password" autoComplete="current-password"
                value={currentPw} onChange={e => setCurrentPw(e.target.value)} />
              <input style={s.input} type="password" placeholder="new password (8+ characters)" autoComplete="new-password"
                value={newPw} onChange={e => setNewPw(e.target.value)} />
              <input style={s.input} type="password" placeholder="new password again" autoComplete="new-password"
                value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
              {pwMismatch && <div style={s.warn}>the new passwords don't match</div>}
              {newPw && newPw.length < 8 && <div style={s.warn}>at least 8 characters</div>}
              <div style={s.row}>
                <button style={pwReady && !savingPw ? s.primaryBtn : s.primaryBtnOff}
                  onClick={savePassword} disabled={!pwReady || savingPw}>
                  {savingPw ? 'saving…' : 'change password'}
                </button>
              </div>
              <div style={s.hint}>changing it signs out every other device.</div>
            </div>
          </div>
        </div>

        {/* DEVICES */}
        <div style={s.section}>
          <div style={s.sectionLabel}>signed-in devices</div>
          <div style={s.list}>
            {sessions.length === 0 && <div style={s.hint}>loading…</div>}
            {sessions.map(x => (
              <div key={x.id} style={{ ...s.card, ...(x.is_current ? s.cardCurrent : {}) }}>
                <div style={s.rowBetween}>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.value}>
                      {deviceName(x.user_agent)}
                      {x.is_current && <span style={s.badge}>this device</span>}
                    </div>
                    <div style={s.hint}>
                      {[placeName(x.location), x.location?.isp].filter(Boolean).join(' · ') || x.ip}
                    </div>
                    <div style={s.hint}>signed in {shortDate(x.signed_in_at)} · {ago(x.last_seen_at)}</div>
                  </div>
                  <button style={s.smallBtn} onClick={() => signOutDevice(x)}>sign out</button>
                </div>
              </div>
            ))}
          </div>
          {otherSessions.length > 0 && (
            !confirmSignOutOthers ? (
              <button style={s.outlineBtn} onClick={() => setConfirmSignOutOthers(true)}>
                sign out all other devices
              </button>
            ) : (
              <div style={s.card}>
                <div style={s.value}>sign out {otherSessions.length} other device{otherSessions.length === 1 ? '' : 's'}?</div>
                <div style={s.hint}>they'll have to sign in again. this device stays signed in.</div>
                <div style={{ ...s.row, marginTop: '10px' }}>
                  <button style={s.dangerBtn} onClick={signOutOthers}>sign them out</button>
                  <button style={s.quietBtn} onClick={() => setConfirmSignOutOthers(false)}>cancel</button>
                </div>
              </div>
            )
          )}
          <div style={s.hint}>
            don't recognise one? sign it out, then change your password.
          </div>
        </div>

        {/* TWO-STEP LOGIN */}
        <div style={s.section}>
          <div style={s.sectionLabel}>two-step login</div>
          <div style={s.card}>
            {tfStage === 'codes' ? (
              <div style={s.form}>
                <div style={s.value}>your backup codes</div>
                <div style={s.hint}>
                  save these somewhere safe — a password manager or a printed copy. each one works
                  once, instead of the app code, if you lose your phone. this is the only time they're shown.
                </div>
                <div style={s.codesGrid}>
                  {backupCodes.map(c => <div key={c} style={s.code}>{c}</div>)}
                </div>
                <div style={s.row}>
                  <button style={s.smallBtn} onClick={() => copy(backupCodes.join('\n'), 'backup codes')}>copy all</button>
                  <button style={s.primaryBtn} onClick={() => { setBackupCodes([]); resetTf(); }}>I've saved them</button>
                </div>
              </div>
            ) : account?.two_factor_enabled ? (
              tfStage === 'disable' || tfStage === 'regen' ? (
                <div style={s.form}>
                  <div style={s.value}>{tfStage === 'disable' ? 'turn off two-step login?' : 'make new backup codes?'}</div>
                  <div style={s.hint}>
                    {tfStage === 'disable'
                      ? 'signing in will only need your password again.'
                      : 'your old backup codes will stop working.'}
                  </div>
                  <input style={s.input} type="password" placeholder="your current password" autoComplete="current-password"
                    value={tfPassword} onChange={e => setTfPassword(e.target.value)} />
                  <div style={s.row}>
                    <button
                      style={tfPassword && !tfBusy ? (tfStage === 'disable' ? s.dangerBtn : s.primaryBtn) : s.primaryBtnOff}
                      onClick={tfStage === 'disable' ? disableTwoFactor : regenerateCodes}
                      disabled={!tfPassword || tfBusy}
                    >
                      {tfBusy ? '…' : tfStage === 'disable' ? 'turn it off' : 'make new codes'}
                    </button>
                    <button style={s.quietBtn} onClick={resetTf}>cancel</button>
                  </div>
                </div>
              ) : (
                <div style={s.form}>
                  <div style={s.value}>
                    on <span style={{ ...s.badge, ...s.badgeGood }}>protected</span>
                  </div>
                  <div style={s.hint}>
                    signing in asks for the 6-digit code from your authenticator app.
                    {' '}{account.backup_codes_left} backup code{account.backup_codes_left === 1 ? '' : 's'} left.
                  </div>
                  <div style={s.row}>
                    <button style={s.smallBtn} onClick={() => { setTfStage('regen'); setTfPassword(''); }}>new backup codes</button>
                    <button style={s.quietBtn} onClick={() => { setTfStage('disable'); setTfPassword(''); }}>turn off</button>
                  </div>
                </div>
              )
            ) : tfStage === 'setup' ? (
              <div style={s.form}>
                <div style={s.step}><span style={s.stepNum}>1</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.value}>add this account to your authenticator app</div>
                    <div style={s.hint}>
                      on your phone, tap the button below (it opens your authenticator app, or iPhone
                      Passwords). or, in the app, choose "enter a setup key" and type the key.
                    </div>
                    <div style={{ ...s.row, marginTop: '8px' }}>
                      <a href={tfUrl} style={s.linkButton}>add to authenticator app</a>
                      <button style={s.smallBtn} onClick={() => copy(tfSecret, 'setup key')}>copy key</button>
                    </div>
                    <div style={s.key}>{groupKey(tfSecret)}</div>
                  </div>
                </div>
                <div style={s.step}><span style={s.stepNum}>2</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.value}>type the 6-digit code the app shows</div>
                    <div style={{ ...s.row, marginTop: '8px' }}>
                      <input
                        style={{ ...s.input, ...s.codeInput }} inputMode="numeric" autoComplete="one-time-code"
                        maxLength={7} placeholder="123456" value={tfCode}
                        onChange={e => setTfCode(e.target.value.replace(/[^0-9]/g, ''))}
                        onKeyDown={e => e.key === 'Enter' && tfCode.length === 6 && enableTwoFactor()}
                      />
                      <button style={tfCode.length === 6 && !tfBusy ? s.primaryBtn : s.primaryBtnOff}
                        onClick={enableTwoFactor} disabled={tfCode.length !== 6 || tfBusy}>
                        {tfBusy ? 'checking…' : 'turn on'}
                      </button>
                    </div>
                  </div>
                </div>
                <button style={{ ...s.quietBtn, alignSelf: 'flex-start' }} onClick={resetTf}>cancel</button>
              </div>
            ) : (
              <div style={s.form}>
                <div style={s.value}>off</div>
                <div style={s.hint}>
                  add a second step to signing in: after your password, the 6-digit code from an
                  authenticator app on your phone (Google Authenticator, Authy, or iPhone Passwords).
                  someone who learns your password still can't get in.
                </div>
                <div style={s.row}>
                  <button style={tfBusy ? s.primaryBtnOff : s.primaryBtn} onClick={startTwoFactor} disabled={tfBusy}>
                    {tfBusy ? '…' : 'turn on two-step login'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* HOMEPAGE */}
        <div style={s.section}>
          <div style={s.sectionLabel}>homepage</div>
          <button style={s.navCard} onClick={() => navigate('/settings/home')}>
            <div style={{ minWidth: 0, textAlign: 'left' }}>
              <div style={s.value}>stickers & cards</div>
              <div style={s.hint}>which stickers show where, and the order and size of the cards</div>
            </div>
            <span style={s.arrow}>→</span>
          </button>
        </div>

        {/* SPENDING */}
        <div style={s.section}>
          <div style={s.sectionLabel}>spending</div>
          <button style={s.navCard} onClick={() => navigate('/settings/spending')}>
            <div style={{ minWidth: 0, textAlign: 'left' }}>
              <div style={s.value}>what the models cost</div>
              <div style={s.hint}>translation, companions, writing and more — today, this week, this month and overall</div>
            </div>
            <span style={s.arrow}>→</span>
          </button>
        </div>

        {/* STUDY */}
        <div style={s.section}>
          <div style={s.sectionLabel}>study</div>
          <button style={s.navCard} onClick={() => navigate('/study/settings')}>
            <div style={{ minWidth: 0, textAlign: 'left' }}>
              <div style={s.value}>study stickers & messages</div>
              <div style={s.hint}>upload stickers and write what he says during a study session</div>
            </div>
            <span style={s.arrow}>→</span>
          </button>
        </div>

        {/* SECURITY LOG */}
        {account?.is_admin && (
          <div style={s.section}>
            <div style={s.sectionLabel}>security</div>
            <button style={s.navCard} onClick={() => navigate('/security')}>
              <div style={{ minWidth: 0, textAlign: 'left' }}>
                <div style={s.value}>visitors & security log</div>
                <div style={s.hint}>who opened the site, from where, and anything that looked shady</div>
              </div>
              <span style={s.arrow}>→</span>
            </button>
          </div>
        )}

        <div style={s.divider} />
        <button style={s.outlineBtn} onClick={logout}>log out of this device</button>
      </div>

      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  container: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontWeight: 300, color: '#e8ddd0', display: 'flex', flexDirection: 'column' },
  header: { padding: '24px 28px 20px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.08)', display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 },
  backBtn: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  headerTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: '#e8ddd0', fontWeight: '300', fontStyle: 'italic' },
  content: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '24px 20px 80px', display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '640px', width: '100%', margin: '0 auto', boxSizing: 'border-box' },
  section: { display: 'flex', flexDirection: 'column', gap: '10px' },
  sectionLabel: { fontSize: '10px', color: '#4a3d30', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: '400', paddingLeft: '2px' },
  card: { background: '#1e1825', borderRadius: '18px', padding: '16px 18px', border: '1px solid rgba(201,147,90,0.08)' },
  cardCurrent: { border: '1px solid rgba(127,199,154,0.25)' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  profileRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  avatarWrap: { position: 'relative', flexShrink: 0, cursor: 'pointer' },
  avatar: { width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: '#c9935a', fontStyle: 'italic', boxShadow: '0 0 0 1px rgba(201,147,90,0.15)', overflow: 'hidden' },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
  avatarBadge: { position: 'absolute', bottom: 0, right: 0, width: '22px', height: '22px', borderRadius: '50%', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', border: '2px solid #1e1825', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  email: { fontSize: '15px', color: '#e8ddd0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  value: { fontSize: '14px', color: '#e8ddd0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  hint: { fontSize: '12px', color: '#6d5d4e', lineHeight: 1.6, marginTop: '2px' },
  warn: { fontSize: '12px', color: '#e0a0a0' },
  rowBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' },
  row: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  form: { display: 'flex', flexDirection: 'column', gap: '10px' },
  input: { width: '100%', boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '12px', padding: '12px 14px', color: '#e8ddd0', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none' },
  codeInput: { width: '130px', letterSpacing: '0.2em', fontVariantNumeric: 'tabular-nums', textAlign: 'center', fontSize: '16px' },
  primaryBtn: { padding: '10px 18px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  primaryBtnOff: { padding: '10px 18px', borderRadius: '12px', border: 'none', background: 'rgba(201,147,90,0.15)', color: 'rgba(201,147,90,0.45)', fontSize: '13px', cursor: 'default', fontFamily: "'DM Sans', sans-serif" },
  smallBtn: { padding: '8px 14px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.2)', background: 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap', flexShrink: 0 },
  quietBtn: { padding: '8px 10px', border: 'none', background: 'transparent', color: '#6d5d4e', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  dangerBtn: { padding: '10px 18px', borderRadius: '12px', border: 'none', background: '#b04a4a', color: '#f4e6e6', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  outlineBtn: { width: '100%', padding: '14px', borderRadius: '14px', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#8a5f35', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer' },
  linkBtn: { background: 'transparent', border: 'none', padding: 0, color: '#c9935a', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  linkButton: { padding: '8px 14px', borderRadius: '12px', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '12px', textDecoration: 'none', fontFamily: "'DM Sans', sans-serif" },
  badge: { padding: '1px 8px', borderRadius: '10px', fontSize: '10.5px', background: 'rgba(127,199,154,0.14)', color: '#9fd4b1' },
  badgeGood: {},
  step: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  stepNum: { width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(201,147,90,0.15)', color: '#c9935a', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
  key: { marginTop: '10px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '14px', letterSpacing: '0.06em', color: '#e8ddd0', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.12)', borderRadius: '10px', padding: '10px 12px', wordBreak: 'break-all', userSelect: 'all' },
  codesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px' },
  code: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '14px', color: '#e8ddd0', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.12)', borderRadius: '8px', padding: '8px 10px', textAlign: 'center', userSelect: 'all' },
  navCard: { width: '100%', background: '#1e1825', borderRadius: '18px', padding: '16px 18px', border: '1px solid rgba(201,147,90,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", color: '#e8ddd0' },
  arrow: { color: '#4a3d30', fontSize: '16px' },
  divider: { height: '1px', background: 'rgba(201,147,90,0.06)' },
  errorBanner: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', wordBreak: 'break-word', cursor: 'pointer' },
  toast: { position: 'fixed', bottom: '32px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(30,24,37,0.96)', border: '1px solid rgba(201,147,90,0.3)', color: '#c9935a', padding: '10px 20px', borderRadius: '20px', fontSize: '13px', zIndex: 300, maxWidth: 'calc(100vw - 32px)', textAlign: 'center' },
};
